/* eslint-disable no-param-reassign */
import { createAction, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import selectSportsbookState from '../../sportsbook-state-selector';
import { RootState, ThunkAction } from '~/store/types';
import { trackSportsbookOpenSearch } from '@comeon/analytics';

type State = {
	query: string;
	isOpen: boolean;
};

// Selectors
export const selectSearchQuery = (state: RootState) => selectSportsbookState(state).search.query;
export const selectIsSearchOpen = (state: RootState) => selectSportsbookState(state).search.isOpen;

// Reducer
const initialState: State = {
	query: '',
	isOpen: false
};

export const searchSlice = createSlice({
	name: 'search',
	initialState,
	reducers: {
		searchToggled: (state, action: PayloadAction<boolean>) => {
			state.isOpen = action.payload;
		},
		queryUpdated: (state, action: PayloadAction<string>) => {
			state.query = action.payload;
		}
	}
});

const { searchToggled } = searchSlice.actions;
export const updateSearchQuery = createAction<string>('search/queryUpdated');

/**
 * Toggles the search open or closed.
 *
 * @param isOpen - Whether the search should be open or closed. If not provided, the search will be toggled.
 */
export const toggleSearch =
	(isOpen?: boolean): ThunkAction =>
	(dispatch, getState) => {
		const isOpenInState = selectIsSearchOpen(getState());
		const nextIsOpen = isOpen ?? !isOpenInState;

		dispatch(searchToggled(nextIsOpen));

		if (nextIsOpen) {
			trackSportsbookOpenSearch();
		}
	};

export const closeSearch =
	(shouldClearQuery = false): ThunkAction =>
	dispatch => {
		dispatch(searchToggled(false));

		if (shouldClearQuery) {
			dispatch(updateSearchQuery(''));
		}
	};

export default searchSlice.reducer;
